/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: pages\SmsProviderFormView.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX SMS Messaging module.
----------------------------------------------------------------*/


import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MessageSquare, Plus, TestTube2 } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { ROUTES } from '../../../constants/routes';
import { ModuleFormHeader } from '../../../components/layouts';
import { FixedActionBar } from '../../../components/ui/FixedActionBar';
import ConfirmationModal from '../../../components/ui/ConfirmationModal';
import { SmsProviderFormData } from '../types';
import { fetchProvider, createProvider, updateProvider, testProvider, deleteProvider } from '../api';
import { PROVIDER_FIELDS } from '../smsProviderConstants';
import { SmsProviderBasicBox } from '../components/SmsProviderBasicBox';
import { SmsProviderCredentialsBox } from '../components/SmsProviderCredentialsBox';

const createDefaultFormData = (): SmsProviderFormData => ({
    name: '', providerType: 'twilio', priority: 100, isDefault: false, enabled: true, rateLimitPerSecond: 10,
});

export default function SmsProviderFormView() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();

    const isEditMode = !!(id && id !== 'new');

    const [formData, setFormData] = useState<SmsProviderFormData>(createDefaultFormData);
    const [originalFormData, setOriginalFormData] = useState<SmsProviderFormData>(createDefaultFormData);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const fieldConfig = useMemo(() => PROVIDER_FIELDS[formData.providerType] || PROVIDER_FIELDS.custom, [formData.providerType]);
    const hasChanges = useMemo(() => JSON.stringify(formData) !== JSON.stringify(originalFormData), [formData, originalFormData]);

    // -- Load --------------------------------------------------
    useEffect(() => {
        if (!isEditMode || !id) return;
        (async () => {
            try {
                setIsLoading(true);
                const p = await fetchProvider(id!);
                if (p) {
                    const d: SmsProviderFormData = { name: p.name, providerType: p.providerType, apiKey: p.apiKey, apiSecret: p.apiSecret, accountSid: p.accountSid, apiEndpoint: p.apiEndpoint, priority: p.priority, isDefault: p.isDefault, enabled: p.enabled, rateLimitPerSecond: p.rateLimitPerSecond };
                    setFormData(d); setOriginalFormData(d);
                }
            } catch (err: any) { showError(err?.message || t('sms.providers.messages.loadError')); }
            finally { setIsLoading(false); }
        })();
    }, [id]);

    // -- Field change ------------------------------------------
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleFieldChange = useCallback((field: keyof SmsProviderFormData, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field as string]) setErrors((prev) => { const { [field as string]: _, ...rest } = prev; return rest; });
    }, [errors]);

    // -- Validation --------------------------------------------
    const validateForm = useCallback(() => {
        const errs: Record<string, string> = {};
        if (!formData.name?.trim()) errs.name = t('validation.required');
        fieldConfig.required.forEach((f) => { if (!formData[f as keyof SmsProviderFormData]) errs[f] = t('validation.required'); });
        setErrors(errs);
        return Object.keys(errs).length === 0;
    }, [formData, fieldConfig, t]);

    // -- Save --------------------------------------------------
    const handleSave = useCallback(async () => {
        if (!validateForm()) { showError(t('sms.providers.messages.validationError', t('common.validationError'))); return undefined; }
        try {
            setIsSaving(true);
            const result = isEditMode ? await updateProvider(id!, formData) : await createProvider(formData);
            showSuccess(t(isEditMode ? 'sms.providers.messages.updateSuccess' : 'sms.providers.messages.createSuccess'));
            setOriginalFormData(formData);
            return result;
        } catch (err: any) { showError(err?.message || t('sms.providers.messages.saveError')); return undefined; }
        finally { setIsSaving(false); }
    }, [formData, isEditMode, id, validateForm, showSuccess, showError, t]);

    const handleSaveWithAction = useCallback(async (action: 'save' | 'save-and-close' | 'save-and-new') => {
        const result = await handleSave();
        if (!result) return;
        if (action === 'save' && !isEditMode && (result.uuid || result.id)) navigate(ROUTES.SMS_PROVIDER_DETAIL(result.uuid ?? result.id));
        else if (action === 'save-and-close') navigate(ROUTES.SMS_PROVIDERS);
        else if (action === 'save-and-new') navigate(`${ROUTES.SMS_PROVIDERS}/new`);
    }, [handleSave, navigate, isEditMode]);

    // -- Cancel ------------------------------------------------
    const handleCancel = useCallback(() => {
        hasChanges ? setFormData(originalFormData) : navigate(ROUTES.SMS_PROVIDERS);
    }, [hasChanges, originalFormData, navigate]);

    // -- Delete ------------------------------------------------
    const handleDeleteConfirm = useCallback(async () => {
        if (!isEditMode || !id) return;
        setIsDeleting(true);
        try { await deleteProvider(id); showSuccess(t('sms.providers.messages.deleteSuccess')); navigate(ROUTES.SMS_PROVIDERS); }
        catch (err: any) { showError(err?.message || t('sms.providers.messages.deleteError')); setShowDeleteModal(false); }
        finally { setIsDeleting(false); }
    }, [isEditMode, id, showSuccess, showError, t, navigate]);

    // -- Test --------------------------------------------------
    const handleTest = useCallback(async () => {
        if (!isEditMode || !id) return;
        setIsTesting(true);
        try {
            const r = await testProvider(id);
            r.connected ? showSuccess(t('sms.providers.messages.testSuccess', { latency: r.latencyMs })) : showError(t('sms.providers.messages.testFailed'));
        } catch (err: any) { showError(err?.message || t('sms.providers.messages.testError')); }
        finally { setIsTesting(false); }
    }, [isEditMode, id, showSuccess, showError, t]);

    if (isLoading) return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">{t('common.loading')}</div></div>;

    return (
        <div className="p-6 pb-20">
            <ModuleFormHeader
                icon={<MessageSquare className="h-5 w-5 text-primary" />}
                title={isEditMode ? `${t('sms.providers.editTitle')} - ${formData.name || ''}` : t('sms.providers.createTitle')}
                backButtonText={t('common.list', 'List')}
                onBack={() => navigate(ROUTES.SMS_PROVIDERS)}
                actions={[
                    ...(isEditMode ? [{ icon: <TestTube2 className={`h-5 w-5 text-muted-foreground ${isTesting ? 'animate-pulse' : ''}`} />, tooltip: t('sms.providers.actions.test'), onClick: handleTest, disabled: isTesting }] : []),
                    { icon: <Plus className="h-5 w-5 text-muted-foreground" />, tooltip: t('sms.providers.actions.add'), onClick: () => navigate(`${ROUTES.SMS_PROVIDERS}/new`) },
                ]}
            />

            <div className="mt-8 space-y-4">
                <SmsProviderBasicBox formData={formData} errors={errors} onFieldChange={handleFieldChange} />
                <SmsProviderCredentialsBox formData={formData} errors={errors} fieldConfig={fieldConfig} onFieldChange={handleFieldChange} />
            </div>

            <FixedActionBar onSaveWithAction={handleSaveWithAction} onCancel={handleCancel} onDelete={() => { if (isEditMode) setShowDeleteModal(true); }} isEditMode={isEditMode} isSaving={isSaving} cancelText={t('common.cancel')} deleteText={t('common.delete')} useSaveDropdown saveDropdownStorageKey="smsProviders:saveAction" saveDisabled={isSaving} />

            <ConfirmationModal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} onConfirm={handleDeleteConfirm} title={t('sms.providers.deleteTitle')} message={t('sms.providers.messages.confirmDelete')} confirmText={t('common.delete')} cancelText={t('common.cancel')} type="danger" isLoading={isDeleting} />
        </div>
    );
}
